import http from '@/api/http'
import { serviceTypesPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface BusinessServiceTypeDetail {
  // Single service type record returned by GET `/business-service-types/{id}`.
  // Differs from the nested `BusinessServiceType` (below, returned grouped by
  // category in the list endpoint) only in that it includes the parent
  // `business_service_name_id`; `price` uses the same effective-price-record
  // shape as the list endpoint (not a raw number).

  id: string
  // Service type id (UUID7).

  duration: string
  // ISO 8601 duration (e.g. `PT2H`).

  price: BusinessServiceTypePrice | null
  // Currently effective price record; null when the API has no price set.

  description: string
  // Human-readable description (may be empty).

  business_service_name_id: string
  // Id of the parent business service name (category) (UUID7).
}

export interface BusinessServiceTypeStats {
  // Aggregate usage statistics returned by GET `/business-service-types/{id}/stats`.

  business_service_type_id: string
  // Back-reference to the parent service type id (UUID7).

  business_service_count: number
  // Total number of service occurrences (sessions) of this type across all time.

  client_visit_count: number
  // Total client visits across all occurrences (sum of `client_ids.length`).

  unique_client_count: number
  // Distinct clients that attended at least one occurrence of this type.

  average_visits_per_client: number
  // `client_visit_count / unique_client_count`; computed server-side for display.

  first_service_date_time: string | null
  // ISO 8601 datetime of the earliest occurrence; null when the type has no services yet.
}

export interface CreateBusinessServiceTypePayload {
  // Request body for POST `/business-service-types`.

  business_service_name_id: string
  // Id of the parent category (from GET /business-service-names) (UUID7).

  duration: string
  // ISO 8601 duration string (e.g. "P3D", "PT1H30M").

  price: {
    start: string
    // ISO 8601 datetime from which the price is effective.

    price: number
    // Price amount in major currency units.
  }

  description?: string
  // Omit when blank.
}

/**
 * Creates a new business service type via POST `/business-service-types`.
 */
export async function createBusinessServiceType(
  payload: CreateBusinessServiceTypePayload,
): Promise<BusinessServiceTypeDetail> {
  const { data } = await http.post<BusinessServiceTypeDetail>('/business-service-types', payload)
  if (import.meta.env.DEV) {
    console.log('[createBusinessServiceType] POST /business-service-types response', data)
  }
  return data
}

/**
 * Fetches a single business service type definition by id for the info card.
 * Returns the flat representation with `business_service_name_id` rather than
 * the grouped shape served by `fetchBusinessServiceTypes`.
 */
export async function fetchBusinessServiceType(id: string): Promise<BusinessServiceTypeDetail> {
  const { data } = await http.get<BusinessServiceTypeDetail>(`/business-service-types/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchBusinessServiceType] GET /business-service-types/${id} response`, data)
  }
  return data
}

/**
 * Fetches aggregate statistics for a service type used by the meta section of
 * the info card. Stats are computed server-side so the client does not have
 * to drain the occurrences stream just to render the five headline numbers.
 */
export async function fetchBusinessServiceTypeStats(id: string): Promise<BusinessServiceTypeStats> {
  const { data } = await http.get<BusinessServiceTypeStats>(`/business-service-types/${id}/stats`)
  if (import.meta.env.DEV) {
    console.log(
      `[fetchBusinessServiceTypeStats] GET /business-service-types/${id}/stats response`,
      data,
    )
  }
  return data
}

export interface BusinessServiceTypePrice {
  // Currently effective price record nested inside a `BusinessServiceType`.

  id: number
  // Price record id.

  start: string
  // ISO datetime from which this price became effective.

  price: number
  // Raw price amount as returned by the backend (no minor-unit conversion).

  business_service_type_id: string
  // Back-reference to the parent service type id (UUID7).
}

export interface BusinessServiceType {
  // One service type definition nested under a business service name (category).

  id: string
  // Service type id (UUID7).

  duration: string
  // ISO 8601 duration (e.g. `PT1H30M`).

  price: BusinessServiceTypePrice | null
  // Currently effective price record; null when the API has no price set.

  description: string
  // Human-readable description (may be empty).
}

/**
 * Returns the current raw price amount for a service type exactly as the
 * backend reports it (no minor-unit conversion or formatting), or `undefined`
 * when the API returned no price record (or a malformed one). Accepts both
 * `BusinessServiceType` (list endpoint) and `BusinessServiceTypeDetail`
 * (detail endpoint) since they share the same `price` record shape.
 */
export function businessServiceTypePriceAmount(type: {
  price: BusinessServiceTypePrice | null
}): number | undefined {
  const price = type.price
  if (price == null) return undefined
  if (typeof price.price !== 'number' || !Number.isFinite(price.price)) {
    return undefined
  }
  return price.price
}

export interface BusinessServiceTypesGroup {
  // One entry from GET `/business-service-types`: all types belonging to a single category.

  business_service_name_id: string
  // Id of the parent business service name (category) (UUID7).

  business_service_types: BusinessServiceType[]
  // Service type definitions for that category.
}

interface BusinessServiceTypesResponse extends PageMeta {
  // Paginated list payload from GET `/business-service-types` (grouped by category id).

  items: BusinessServiceTypesGroup[]
  // Page of category groups, each with its nested service types.
}

export interface UpdateBusinessServiceTypePayload {
  duration: string
  price: { start: string; price: number }
  description: string
}

export async function updateBusinessServiceType(
  id: string,
  payload: UpdateBusinessServiceTypePayload,
): Promise<BusinessServiceTypeDetail> {
  const { data } = await http.patch<BusinessServiceTypeDetail>(
    `/business-service-types/${id}`,
    payload,
  )
  if (import.meta.env.DEV)
    console.log(`[updateBusinessServiceType] PATCH /business-service-types/${id} response`, data)
  return data
}

/**
 * Fetches a page of service types grouped by business service name.
 * Same cursor/limit contract as the other paginated list fetchers.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchBusinessServiceTypes(
  cursor?: number,
): Promise<BusinessServiceTypesResponse> {
  const { data } = await http.get<BusinessServiceTypesResponse>('/business-service-types', {
    params: cursorParams(serviceTypesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchBusinessServiceTypes] GET /business-service-types response', data)
  }
  return data
}

/**
 * Deletes a business service type by id.
 * @param id - Service type id to delete.
 */
export async function deleteBusinessServiceType(id: string): Promise<void> {
  await http.delete(`/business-service-types/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteBusinessServiceType] DELETE /business-service-types/${id}`)
  }
}
